import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  makeStyles,
  mergeClasses,
  Radio,
  RadioGroup,
  tokens,
} from "@fluentui/react-components";
import React, { useEffect } from "react";
import { AddFilled } from "@fluentui/react-icons";
import parkingTimerBlack from "../../symbols/parking_timer_black.svg";
import parkingForbidden from "../../symbols/parking_forbidden.svg";
import stopForbidden from "../../symbols/stop_forbidden.svg";
import parking from "../../symbols/p_symbol_black.svg";
import { ISignForm, SignColor, SymbolPlacement } from "../../interfaces/ISignForm";
import { UseFormReturn } from "react-hook-form";
import { ISymbol } from "../../interfaces/ISymbol";
import { useTranslation } from "react-i18next";
type SignSymbolFormProps = {
  signForm: UseFormReturn<ISignForm, any, ISignForm>;
};
const useStyles = makeStyles({
  flex: { display: "flex" },
  selectedSymbol: {
    display: "flex",
    alignItems: "flex-start",
    marginTop: tokens.spacingVerticalL,
  },
  selectedSymbolInModal: {
    backgroundColor: "rgba(0, 123, 255, 0.1)",
    transform: "scale(1.1)",
  },
  button: {
    marginTop: tokens.spacingVerticalXXL,
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase400,
    marginBottom: "56px",
    color: tokens.colorBrandForeground1,
  },
  icon: {
    marginRight: tokens.spacingHorizontalS,
  },
  symbolContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: tokens.spacingVerticalL,
  },
  symbol: {
    width: "100px",
    height: "100px",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    cursor: "pointer",
    transition: "transform 0.2s ease",
  },
  symbolHoverEffect: {
    "&:hover": {
      transform: "scale(1.1)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    },
  },
  disabledSymbol: {
    cursor: "not-allowed",
    filter: "grayscale(30%)",
    opacity: 0.5,
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
  field: {
    width: "100%",
    fontWeight: tokens.fontWeightBold,
    marginTop: tokens.spacingVerticalXXL,
  },
  widthHundred: {
    width: "100%",
  },
  iconMargin: {
    marginRight: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalL,
  },
  inputHeight: {
    height: tokens.spacingVerticalXXXL,
  },
});
export const SignSymbolForm = ({ signForm }: SignSymbolFormProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { setValue, watch } = signForm;
  const selectedSymbol = watch("symbol");
  const selectedPlacement = watch("symbolPlacement");
  const signHeight = watch("height");

  const topLeftAvailable =
    selectedSymbol?.name === "P-symbol" || signHeight > 200;

  useEffect(() => {
    if (!topLeftAvailable) {
      setValue("symbolPlacement", SymbolPlacement.CENTER);
    }
  }, [topLeftAvailable]);
  const symbols: ISymbol[] = [
    {
      symbol: parking,
      name: "P-symbol",
      width: 90,
      height: 90,
      disabled: watch("primaryColor") === SignColor.YELLOW,
    },
    {
      symbol: parkingTimerBlack,
      name: "Parkeringsskiva",
      width: 100,
      height: 150,
      disabled: watch("primaryColor") === SignColor.YELLOW,
    },
    {
      symbol: parkingForbidden,
      name: "Parkeringsförbud",
      width: 120,
      height: 120,
      disabled: watch("primaryColor") === SignColor.BLUE,
    },
    {
      symbol: stopForbidden,
      name: "Stoppförbud",
      width: 120,
      height: 120,
      disabled: watch("primaryColor") === SignColor.BLUE,
    },
  ];

  return (
    <div>
      <Dialog>
        <div>
          <DialogTrigger disableButtonEnhancement>
            {selectedSymbol && selectedSymbol?.name && (
              <div className={styles.selectedSymbol}>
                <div
                  className={mergeClasses(
                    styles.symbol,
                    styles.symbolHoverEffect
                  )}
                  style={{
                    backgroundImage: `url(${
                      symbols.find((s) => s.name === selectedSymbol?.name)
                        ?.symbol
                    })`,
                  }}
                ></div>
              </div>
            )}
          </DialogTrigger>
          <DialogTrigger disableButtonEnhancement>
            <Button className={styles.button} appearance="subtle">
              <AddFilled className={styles.icon} />
              {t("button.addSymbol")}
            </Button>
          </DialogTrigger>
        </div>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("label.selectSymbol")}</DialogTitle>
            <DialogContent>
              <div className={styles.symbolContainer}>
                <div className={styles.flex}>
                  {symbols.map((symbol, i) => (
                    <div
                      key={i}
                      className={mergeClasses(
                        styles.symbol,
                        styles.symbolHoverEffect,
                        styles.iconMargin,
                        symbol?.disabled && styles.disabledSymbol,
                        symbol.name === selectedSymbol?.name &&
                          styles.selectedSymbolInModal
                      )}
                      style={{
                        backgroundImage: `url(${symbol.symbol})`,
                      }}
                      onClick={() => {
                        if (symbol.disabled) return;
                        const isSelected = symbol.name === selectedSymbol?.name;
                        setValue("symbol", isSelected ? null : symbol);
                        setValue(
                          "symbolPlacement",
                          isSelected ? undefined : SymbolPlacement.CENTER
                        );
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button
                  appearance="secondary"
                  size="large"
                  onClick={() => {
                    if (!selectedSymbol?.name) {
                      setValue("symbol", null);
                    }
                  }}
                >
                  {t("button.close")}
                </Button>
              </DialogTrigger>
              <DialogTrigger disableButtonEnhancement>
                <Button
                  appearance="primary"
                  size="large"
                  disabled={!selectedSymbol?.name}
                >
                  {t("button.select")}
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {selectedSymbol?.name && (
        <Field
          label={t("formSection.symbolPlacement")}
          className={styles.field}
          size="medium"
        >
          <RadioGroup
            value={selectedPlacement ?? SymbolPlacement.CENTER}
            onChange={(_, data) =>
              setValue("symbolPlacement", data.value as SymbolPlacement)
            }
            layout="horizontal"
          >
            <Radio
              value={SymbolPlacement.CENTER}
              label={t("symbolPlacement.center")}
            />
            {topLeftAvailable && (
              <Radio
                value={SymbolPlacement.TOP_LEFT}
                label={t("symbolPlacement.topLeft")}
              />
            )}
          </RadioGroup>
        </Field>
      )}
    </div>
  );
};
